/** Copyright 2025 SAP SE or an SAP affiliate company. All rights reserved. */
import { createAction, createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import * as PxApiTypes from '@sap-px/pxapi/dist/api/common/Types';
import { feedbackService } from '@/utils/FeedbackService';
import { ThemeId } from '@sap-px/pxapi';
import { getStoredOrBrowserLanguage } from '@/utils/langUtils';

const currentLanguage = getStoredOrBrowserLanguage();
interface FeedbackState {
    pxApiInitialized: boolean;
    invitationDialogIsOpen: boolean;
    pushType: string;
    appContextData: PxApiTypes.AppContextData;
}

const initialState: FeedbackState = {
    pxApiInitialized: false,
    invitationDialogIsOpen: false,
    pushType: '',
    appContextData: {
        appId: 'deployment hub',
        appTitle: 'deployment hub',
        appVersion: '1.0',
        appSupportInfo: '',
        technicalAppComponentId: '',
        appFrameworkId: 'React',
        appFrameworkVersion: '1.0',
        theme: ThemeId.sap_fiori_3,
        languageTag: currentLanguage === 'fallback' ? 'English' : currentLanguage,
    },
};

export const initializePxApi = createAsyncThunk('initializePxApi', async (configData: { configUrl: string; unitId: string; environment: string }) => {
    const isInitialized = await feedbackService.initializePxApi(configData);

    return isInitialized;
});

export const triggerUserInitiatedFeedback = createAsyncThunk('triggerUserInitiatedFeedback', async () => {
    await feedbackService.triggerUserInitiatedFeedback();
});

export const triggerInstantFeedback = createAsyncThunk('triggerInstantFeedback', async (configData: { areaId: string; triggerName: string }) => {
    await feedbackService.triggerInstantFeedback(configData);
});

export const modifyAppContextData = createAction('modifyAppContextData', (payload: Partial<PxApiTypes.AppContextData>) => {
    feedbackService.modifyAppContextData(payload);
    return {
        payload,
    };
});

export const resetAppContextData = createAction('resetAppContextData', () => {
    feedbackService.resetAppContextData(initialState.appContextData);
    return {
        payload: null,
    };
});

export const provideFeedback = createAction('provideFeedback', () => {
    feedbackService.provideFeedback();
    return {
        payload: null,
    };
});

export const declineFeedback = createAction('declineFeedback', () => {
    feedbackService.declineFeedback();
    return {
        payload: null,
    };
});

export const destroyPxApi = createAsyncThunk('destroyPxApi', async () => {
    feedbackService.destroy();
    return true;
});

const feedbackSlice = createSlice({
    name: 'feedback',
    initialState,
    reducers: {
        destroyPxApi(state) {
            state.pxApiInitialized = false;
        },
        setInvitationDialogStatus(state, action: PayloadAction<boolean>) {
            state.invitationDialogIsOpen = action.payload;
        },
        setPushType(state, action: PayloadAction<string>) {
            state.pushType = action.payload;
        },
    },
    extraReducers: builder => {
        builder.addCase(initializePxApi.fulfilled, (state, action: PayloadAction<boolean>) => {
            state.pxApiInitialized = action.payload;
        });
        builder.addCase(triggerUserInitiatedFeedback.fulfilled, () => {});

        builder.addCase(triggerInstantFeedback.fulfilled, () => {});

        builder.addCase(destroyPxApi.fulfilled, (state, action: PayloadAction<boolean>) => {
            state.pxApiInitialized = action.payload;
        });

        builder.addCase(modifyAppContextData, (state, action: PayloadAction<Partial<PxApiTypes.AppContextData>>) => {
            state.appContextData = { ...state.appContextData, ...action.payload };
        });

        builder.addCase(resetAppContextData, state => {
            state.appContextData = initialState.appContextData;
        });

        builder.addCase(provideFeedback, state => {
            state.invitationDialogIsOpen = false;
        });

        builder.addCase(declineFeedback, state => {
            state.invitationDialogIsOpen = false;
        });
    },
});

export const actions = feedbackSlice.actions;

export { feedbackSlice };
export default feedbackSlice.reducer;
